// cSpell:disable-next-line
import { Player } from '@lottiefiles/react-lottie-player';
import { appEnv } from 'app/core/environment';
import AppImage from 'app/shared/components/app-image/AppImage';
import Button from 'app/shared/components/button/Button';
import { IWarrantyLogoSvg } from 'app/shared/components/icon/icon-lib';
import Body from 'app/shared/components/typography/body/Body';
import Heading from 'app/shared/components/typography/heading/Heading';
import Subheading from 'app/shared/components/typography/subheading/Subheading';
import SharedRoutePath from 'app/shared/SharedRoutePath';
import styledFuncs from 'app/styles/styledFuncs';
import styledMixins from 'app/styles/styledMixins';
import { LSKey } from 'app/tools/hooks/local-storage/local-storage.types';
import useLocalState from 'app/tools/hooks/local-storage/use-local-storage';
import {
  useEffectOnce,
  useTimeMountedExceeds,
} from 'app/tools/hooks/misc-hooks';
import useSafeAreaInsets from 'app/tools/hooks/use-safe-area-insets';
import logoAnimationJson from 'assets/logo-animation.json';
import React, { useRef, useState } from 'react';
import { Carousel } from 'react-responsive-carousel';
import { Redirect } from 'react-router-dom';
import styled, { css } from 'styled-components/macro';
import { FixedLengthArray } from 'type-fest';
import { TopBannerText } from '../components/misc';

const Container = styled.div`
  ${styledMixins.fullsize}
  ${styledMixins.flexCenter}
  flex: 1 1 100%;
  #logo-animation {
    padding: 5% 8% 0;
  }
`;

const AnimationBox = styled.div`
  position: absolute;
  z-index: 1;
  ${styledMixins.fullsize}
  background: white;
  ${styledMixins.flexCenter}
`;

const Slide = styled.div``;
const StyledCarousel = styled(Carousel)`
  &,
  .carousel-slider,
  .slider-wrapper,
  ${Slide} {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
  }
  .slider {
    flex: 1 1 auto;
  }
  &.carousel-root {
    flex: 4 1 0;
  }
`;

const IWarrantyLogo = styled(AppImage).attrs({
  fallbackImgNode: <IWarrantyLogoSvg />,
  height: '80px',
  alt: 'logo',
})``;

const TopBox = styled.div`
  flex: 1 0 160px;
  max-height: 250px;
  ${styledMixins.flexCenter}
  justify-content: space-evenly;
  > * {
    padding: 0;
    margin: 0;
  }
`;

const SlideImage = styled(AppImage).attrs({
  alt: 'welcome slide',
})`
  padding: 0;
  flex: 3 1 50px;
  min-height: 0;
  overflow: hidden;
  ${styledMixins.flexCenter}
  img {
    object-fit: cover;
    flex: 1 1 0;
    width: 100% !important;
  }
`;

const Subtitle = styled(Subheading)`
  font-size: ${styledFuncs.pxToRem(16)};
  font-weight: 500;
  line-height: 1.3;
  margin: ${styledFuncs.pad(0.5)};
`;

const BottomContainer = styled.div`
  ${styledMixins.flexCenter}
  justify-content: space-around;
  width: 100%;
  padding: 15px 40px;
  flex: 1 1 0;
  > * {
    margin: 4px;
    line-height: 1;
  }
  > *:nth-child(2) {
    margin: 10px 0;
  }
  > button {
    align-self: stretch;
  }
`;

export type SlideInfo = {
  title: string;
  subtitle: FixedLengthArray<string, 2>;
  imgSrc: string;
};

type Props = {
  slides: SlideInfo[];
};
export default function Welcome({ slides }: Props): JSX.Element {
  const player = useRef<Player>(null);

  const [fourSecondsPassed] = useTimeMountedExceeds(4000);
  const [currentSlide, setCurrentSlide] = useState(0);

  const [welcomed, setWelcomed] = useLocalState(
    LSKey.WELCOMED,
    false
  );
  const [refCode] = useLocalState(LSKey.REF_CODE, '');

  useEffectOnce(() => {
    setTimeout(() => player.current?.play(), 800);
  });

  const { top, bottom } = useSafeAreaInsets();

  if (fourSecondsPassed && refCode && !welcomed) {
    // NOTE: bypasses welcome screens if refCode present
    setWelcomed(true);
  }
  if (welcomed) {
    return <Redirect to={SharedRoutePath.SIGN_UP} />;
  }

  return (
    <Container
      css={css`
        ${appEnv.APP_VARIANT !== 'business' &&
        css`
          // NOTE: no top padding on business variant due to top text banner
          padding-top: ${top}px;
        `}
        padding-bottom: ${bottom}px;
      `}
    >
      {appEnv.APP_VARIANT === 'business' && (
        <TopBannerText antiMargin={false} expandBelow={false}>
          Business App
        </TopBannerText>
      )}
      <StyledCarousel
        emulateTouch
        swipeable
        onChange={setCurrentSlide}
        selectedItem={currentSlide}
        showThumbs={false}
        infiniteLoop={false}
        showStatus={false}
        showArrows={false}
        autoPlay={false}
      >
        {slides.map(slideInfo => (
          <Slide key={slideInfo.title}>
            <TopBox>
              <IWarrantyLogo />
              <Heading variant="h4" color="primary">
                {slideInfo.title}
              </Heading>
              <Subtitle color="primary">
                {slideInfo.subtitle[0]}
                <br />
                {slideInfo.subtitle[1]}
              </Subtitle>
            </TopBox>
            <SlideImage src={slideInfo.imgSrc} />
          </Slide>
        ))}
      </StyledCarousel>
      <BottomContainer>
        <Body centered tiny color="primary">
          {currentSlide !== 2
            ? 'Swipe to see more'
            : 'Click below to begin!'}
        </Body>
        <Button onClick={() => setWelcomed(true)}>Get started</Button>
      </BottomContainer>

      {!fourSecondsPassed && (
        <AnimationBox>
          <Player
            id="logo-animation"
            src={logoAnimationJson}
            ref={player}
            keepLastFrame
          />
        </AnimationBox>
      )}
    </Container>
  );
}
